import { Upload, message } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import type { UploadFile, RcFile } from "antd/lib/upload/interface";
import React, { useEffect, useState } from "react";
import type { GeoJsonObject } from "geojson";

const { Dragger } = Upload;

const UploadGeoJSON = ({
  setFile,
  file,
}: {
  setFile: (file: GeoJsonObject | null) => void;
  file: GeoJsonObject | null;
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  useEffect(() => {
    if (!file) {
      setFileList([]);
    } else {
      setFileList([
        {
          uid: "-1",
          name: "geojson-upload.json",
          status: "done",
        } as UploadFile,
      ]);
    }
  }, [file]);

  const beforeUpload = (file: RcFile) => {
    const isGeoJSON =
      file.type === "application/json" ||
      file.type === "application/geo+json" ||
      file.name.endsWith(".geojson");

    if (!isGeoJSON) {
      message.error("Only .geojson files are allowed!");
      return false;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("GeoJSON file must be smaller than 5MB!");
      return false;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const parsed = JSON.parse(content);

        // === Top-Level Key Validation ===
        const allowedTopLevelKeys = ["type", "features", "bbox", "id"];
        const topLevelKeys = Object.keys(parsed);
        for (const key of topLevelKeys) {
          if (!allowedTopLevelKeys.includes(key)) {
            throw new Error(
              `Invalid top-level field "${key}" — not allowed in RFC 7946`
            );
          }
        }

        if (!parsed?.type) {
          throw new Error("Missing required field 'type'");
        }

        // === Type Validation ===
        if (parsed.type === "FeatureCollection") {
          if (!Array.isArray(parsed.features)) {
            throw new Error(
              "GeoJSON FeatureCollection must include a 'features' array"
            );
          }

          for (const feature of parsed.features) {
            if (feature.type !== "Feature") {
              throw new Error("Each item in 'features' must have type 'Feature'");
            }

            if (!validateGeometry(feature.geometry)) {
              throw new Error("Invalid geometry in one or more features");
            }
          }
        } else if (parsed.type === "Feature") {
          if (!validateGeometry(parsed.geometry)) {
            throw new Error("Invalid geometry in Feature");
          }
        } else if (!validateGeometry(parsed)) {
          throw new Error("Invalid top-level GeoJSON type or geometry");
        }

        setFile(parsed);
        message.success("GeoJSON parsed and validated successfully");
      } catch (err: any) {
        console.error("GeoJSON validation error:", err);
        message.error(`Invalid GeoJSON: ${err.message || "Parsing failed"}`);
        setFile(null);
      }
    };

    reader.readAsText(file);
    return false;
  };

  const handleRemove = () => {
    setFile(null);
    setFileList([]);
  };

  return (
    <Dragger
      name="geojson"
      fileList={fileList}
      beforeUpload={beforeUpload}
      onRemove={handleRemove}
      accept=".geojson,application/json"
      multiple={false}
      showUploadList={{ showRemoveIcon: true }}
      style={{
        padding: "12px",
        background: "#fafafa",
        border: "1px dashed #d9d9d9",
        borderRadius: 4,
      }}
    >
      <p className="ant-upload-drag-icon">
        <InboxOutlined style={{ fontSize: 28, color: "#1890ff" }} />
      </p>
      <p className="ant-upload-text">
        Click or drag a GeoJSON file to this area to upload
      </p>
    </Dragger>
  );
};

export default UploadGeoJSON;

// === Geometry Validator ===
function validateGeometry(geometry: any): boolean {
  if (!geometry || typeof geometry !== "object") return false;
  if (!geometry.type || !geometry.coordinates) return false;

  const validTypes = [
    "Point",
    "MultiPoint",
    "LineString",
    "MultiLineString",
    "Polygon",
    "MultiPolygon",
    "GeometryCollection",
  ];

  if (!validTypes.includes(geometry.type)) return false;

  const isNumericArray = (coords: any): boolean =>
    Array.isArray(coords) &&
    coords.every((c) =>
      Array.isArray(c)
        ? isNumericArray(c)
        : typeof c === "number" && Number.isFinite(c)
    );

  return isNumericArray(geometry.coordinates);
}
